import { Answer, ScoreBreakdown, SurveyResponse } from "@/types/survey";
import { questions } from "@/data/questions";

// Get numeric value for any answer type
const getNumericValue = (answer: Answer): number => {
  if (typeof answer === 'number') return answer;
  if (answer === 'Y') return 5;
  if (answer === 'N') return 1;
  if (typeof answer === 'string') {
    // For dropdown emotional responses
    switch (answer.toLowerCase()) {
      case 'joy': return 5;
      case 'trust': return 4; 
      case 'indifference': return 2;
      case 'frustration': return 1;
      default: return 3; // Default for other string values
    }
  }
  return 0;
};

// Calculate weighted average score for a category
const calculateCategoryAverage = (
  responses: SurveyResponse,
  category: 'ea' | 'elv' | 'l' | 'tb' | 'bc'
): { average: number, weightedAverage: number } => {
  const categoryQuestions = questions.filter(q => q.category === category);
  let total = 0;
  let weightedTotal = 0;
  let count = 0;
  let totalWeight = 0;

  categoryQuestions.forEach(question => {
    const response = responses[question.id];
    
    if (response !== undefined && question.type !== 'text') {
      let value = getNumericValue(response);
      
      // Handle inverted scales (like FC where lower is better)
      if (question.inverted && question.scale) {
        value = (question.scale + 1) - value;
      }
      
      // Apply weight if defined, otherwise use 1
      const weight = question.weight || 1;
      
      total += value;
      weightedTotal += value * weight;
      count++;
      totalWeight += weight;
    }
  });

  return {
    average: count > 0 ? total / count : 0,
    weightedAverage: totalWeight > 0 ? weightedTotal / totalWeight : 0
  };
};

// Calculate sentiment score for text responses
const calculateSentimentScore = (text: string): number => {
  // This is a simplified sentiment analysis
  // In a real implementation, this would use natural language processing
  const positiveWords = ['good', 'great', 'excellent', 'love', 'enjoy', 'easy', 'helpful'];
  const negativeWords = ['bad', 'poor', 'terrible', 'hate', 'difficult', 'confusing', 'frustrating'];
  
  const lowerText = text.toLowerCase();
  let score = 3; // Neutral starting point on 1-5 scale
  
  // Count positive and negative words
  let positiveCount = 0;
  let negativeCount = 0;
  
  positiveWords.forEach(word => {
    if (lowerText.includes(word)) positiveCount++;
  });
  
  negativeWords.forEach(word => {
    if (lowerText.includes(word)) negativeCount++;
  });
  
  // Simple algorithm to adjust score
  if (positiveCount > negativeCount) {
    score += Math.min(2, positiveCount - negativeCount);
  } else if (negativeCount > positiveCount) {
    score -= Math.min(2, negativeCount - positiveCount);
  }
  
  // Ensure within 1-5 range
  return Math.max(1, Math.min(5, score));
};

// Extract sentiment tags from text
const extractSentimentTags = (text: string): string[] => {
  const tags = [];
  
  // This is a very simplified approach for demonstration
  // In a real implementation, use NLP or ML models
  if (text.toLowerCase().includes('trust')) tags.push('Trust concerns');
  if (text.toLowerCase().includes('frustrat')) tags.push('Frustration detected');
  if (text.toLowerCase().includes('confus')) tags.push('Confusion issues');
  if (text.toLowerCase().includes('slow') || text.toLowerCase().includes('fast')) tags.push('Speed concerns');
  if (text.toLowerCase().includes('recommend') || text.toLowerCase().includes('suggest')) tags.push('Recommendation potential');
  
  return tags.length > 0 ? tags : ['No specific sentiment detected'];
};

// Get industry benchmark for a given industry
const getIndustryBenchmark = (industry: string): number => {
  const benchmarks: {[key: string]: number} = {
    'Technology': 85,
    'Finance': 80,
    'Healthcare': 78,
    'Education': 75,
    'Entertainment': 82,
    'Retail': 75,
    'Manufacturing': 72,
    'Energy': 76,
    'Hospitality': 79,
    'Food & Beverages': 77,
    'Web 3.0': 88,
    'Other': 75
  };
  
  return benchmarks[industry] || 75; // Default benchmark if industry not found
};

// Calculate NEV score
export const calculateNEV = (
  responses: SurveyResponse,
  industry?: string
): ScoreBreakdown => {
  // Reset session storage to prevent old results from appearing for new surveys
  sessionStorage.removeItem('selectedSurvey');
  
  // Calculate raw averages for each category
  const eaResult = calculateCategoryAverage(responses, 'ea');
  const elvResult = calculateCategoryAverage(responses, 'elv');
  const lResult = calculateCategoryAverage(responses, 'l');
  const tbResult = calculateCategoryAverage(responses, 'tb');
  const bcResult = calculateCategoryAverage(responses, 'bc');

  // Extract averages and weighted averages
  const eaAvg = eaResult.average;
  const elvAvg = elvResult.average;
  const lAvg = lResult.average;
  const tbAvg = tbResult.average;
  const bcAvg = bcResult.average;
  
  const eaWeightedAvg = eaResult.weightedAverage;
  const elvWeightedAvg = elvResult.weightedAverage;
  const lWeightedAvg = lResult.weightedAverage;
  const tbWeightedAvg = tbResult.weightedAverage;
  const bcWeightedAvg = bcResult.weightedAverage;

  console.log('Raw category averages:', { eaAvg, elvAvg, lAvg, tbAvg, bcAvg });
  console.log('Weighted averages:', { eaWeightedAvg, elvWeightedAvg, lWeightedAvg, tbWeightedAvg, bcWeightedAvg });

  // Ensure averages stay within 1-5 range
  const boundedEaAvg = Math.max(1, Math.min(5, eaAvg || 3));
  const boundedElvAvg = Math.max(1, Math.min(5, elvAvg || 3));
  const boundedLAvg = Math.max(1, Math.min(5, lAvg || 3));
  const boundedTbAvg = Math.max(1, Math.min(5, tbAvg || 3));
  const boundedBcAvg = Math.max(1, Math.min(5, bcAvg || 3));
  
  // Bounded weighted averages
  const boundedEaWeightedAvg = Math.max(1, Math.min(5, eaWeightedAvg || 3));
  const boundedElvWeightedAvg = Math.max(1, Math.min(5, elvWeightedAvg || 3));
  const boundedLWeightedAvg = Math.max(1, Math.min(5, lWeightedAvg || 3));
  const boundedTbWeightedAvg = Math.max(1, Math.min(5, tbWeightedAvg || 3));
  const boundedBcWeightedAvg = Math.max(1, Math.min(5, bcWeightedAvg || 3));
  
  // Calculate weighted NEV score to ensure different inputs lead to different scores
  // Modified formula with more differentiation based on user responses
  const weightedEaScore = boundedEaWeightedAvg * 0.4; // Increased weight for emotional attachment
  const weightedElvScore = boundedElvWeightedAvg * 0.3;
  const weightedLScore = boundedLWeightedAvg * 0.3;
  
  // Scale to 0-10 range for the BEAM Score
  const nevScore = (weightedEaScore + weightedElvScore + weightedLScore) * 2;
  
  // Scale NEV to 0-50 range instead of 0-100
  const scaledNEV = Math.min(50, nevScore * 5);
  
  // Calculate EROI more accurately with a cap of 5.0
  const businessContinuityRate = boundedBcWeightedAvg / 5; // Normalize to 0-1 range
  const frictionCoefficient = Math.max(0.5, boundedLWeightedAvg / 5); // Ensure minimum value
  
  // Adjust EROI calculation to have a more balanced range
  const eroi = Math.min(5.0, (nevScore * businessContinuityRate) / (frictionCoefficient * 2));

  // Process text responses for sentiment analysis
  const textSentiments: {[questionId: number]: {score: number; tags: string[]}} = {};
  const textQuestions = questions.filter(q => q.type === 'text');
  
  textQuestions.forEach(q => {
    const response = responses[q.id];
    if (typeof response === 'string' && response.length > 0) {
      const sentimentScore = calculateSentimentScore(response);
      const tags = extractSentimentTags(response);
      textSentiments[q.id] = {
        score: sentimentScore,
        tags: tags
      };
    }
  });

  // Determine rating based on NEV score
  let rating: 'High Risk' | 'Competitive' | 'Exceptional';
  const roundedScore = Math.round(scaledNEV * 10) / 10;
  
  if (roundedScore < 30) {
    rating = 'High Risk';
  } else if (roundedScore < 45) {
    rating = 'Competitive';
  } else {
    rating = 'Exceptional';
  }
  
  // Determine certification level based on normalized score
  let certification: 'Gold' | 'Silver' | 'Bronze' | null = null;
  if (roundedScore >= 90) {
    certification = 'Gold';
  } else if (roundedScore >= 80) {
    certification = 'Silver';
  } else if (roundedScore >= 70) {
    certification = 'Bronze';
  }

  // Generate feedback for each category
  const eaFeedback = generateCategoryFeedback('ea', boundedEaWeightedAvg);
  const elvFeedback = generateCategoryFeedback('elv', boundedElvWeightedAvg);
  const lFeedback = generateCategoryFeedback('l', boundedLWeightedAvg);
  const tbFeedback = generateCategoryFeedback('tb', boundedTbWeightedAvg);
  
  // Generate emotional heatmap data
  const emotionalHeatmap = {
    joy: 0,
    trust: 0,
    frustration: 0,
    indifference: 0,
    other: 0
  };
  
  // Count emotions from dropdown responses
  Object.values(responses).forEach(answer => {
    if (typeof answer === 'string') {
      if (answer.toLowerCase() === 'joy') emotionalHeatmap.joy++;
      else if (answer.toLowerCase() === 'trust') emotionalHeatmap.trust++;
      else if (answer.toLowerCase() === 'frustration') emotionalHeatmap.frustration++;
      else if (answer.toLowerCase() === 'indifference') emotionalHeatmap.indifference++;
      else emotionalHeatmap.other++;
    }
  });
  
  // Generate recommendations based on lowest scores
  const recommendations = generateRecommendations(responses, boundedEaWeightedAvg, boundedElvWeightedAvg, boundedLWeightedAvg, boundedTbWeightedAvg);

  // Generate dominant emotions
  const emotions = ['joy', 'trust', 'frustration', 'indifference'];
  const dominantEmotions = emotions
    .filter(emotion => emotionalHeatmap[emotion as keyof typeof emotionalHeatmap] > 0)
    .sort((a, b) => emotionalHeatmap[b as keyof typeof emotionalHeatmap] - emotionalHeatmap[a as keyof typeof emotionalHeatmap])
    .slice(0, 2);

  // Generate sentiment alert for founder commitment
  const pprAlert = boundedLWeightedAvg < 2.5 
    ? `⚠️ Low founder commitment detected (${boundedLWeightedAvg.toFixed(1)}/5)`
    : null;

  // Create category scores for main categories
  const eaScore = {
    score: boundedEaAvg * 4,
    total: 20,
    percentage: (boundedEaWeightedAvg / 5) * 100,
    feedback: eaFeedback,
    weightedScore: boundedEaWeightedAvg * 4,
  };

  const elvScore = {
    score: boundedElvAvg * 4,
    total: 20,
    percentage: (boundedElvWeightedAvg / 5) * 100,
    feedback: elvFeedback,
    weightedScore: boundedElvWeightedAvg * 4,
  };

  const lScore = {
    score: boundedLAvg * 4,
    total: 20,
    percentage: (boundedLWeightedAvg / 5) * 100,
    feedback: lFeedback,
    weightedScore: boundedLWeightedAvg * 4,
  };

  const tbScore = {
    score: boundedTbAvg * 4,
    total: 20,
    percentage: (boundedTbWeightedAvg / 5) * 100,
    feedback: tbFeedback,
    weightedScore: boundedTbWeightedAvg * 4,
  };

  const bcScore = {
    score: boundedBcAvg * 4,
    total: 20,
    percentage: (boundedBcWeightedAvg / 5) * 100,
    feedback: "Business continuity assessment",
    weightedScore: boundedBcWeightedAvg * 4,
  };
  
  // Generate industry benchmarks
  const industryBenchmarks: {[key: string]: number} = {
    'Technology': 85,
    'Finance': 80,
    'Healthcare': 78,
    'Education': 75,
    'Entertainment': 82,
    'Retail': 75,
    'Manufacturing': 72,
    'Energy': 76,
    'Hospitality': 79,
    'Food & Beverages': 77,
    'Web 3.0': 88,
    'Other': 75
  };

  // Generate industry-specific benchmarks and redFlags based on responses
  const redFlags = generateRedFlags(responses, boundedEaWeightedAvg, boundedElvWeightedAvg, boundedLWeightedAvg);
  const brightSpots = generateBrightSpots(responses, boundedEaWeightedAvg, boundedElvWeightedAvg, boundedLWeightedAvg, boundedTbWeightedAvg);

  // Create categories object for the chart
  const categories = {
    ee: (boundedEaWeightedAvg / 5) * 100,
    lq: (boundedElvWeightedAvg / 5) * 100,
    fc: (boundedLWeightedAvg / 5) * 100,
    tb: (boundedTbWeightedAvg / 5) * 100,
    bc: (boundedBcWeightedAvg / 5) * 100,
  };
  
  // Create historical data (in a real app, this would be stored in a database)
  // For now, we'll create some mock historical data
  const currentDate = new Date().toISOString().split('T')[0];
  const historicalData = [
    { date: "2024-01-15", score: Math.round((scaledNEV - 5 + Math.random() * 10) * 10) / 10 },
    { date: "2024-02-15", score: Math.round((scaledNEV - 3 + Math.random() * 6) * 10) / 10 },
    { date: "2024-03-15", score: Math.round((scaledNEV - 2 + Math.random() * 4) * 10) / 10 },
    { date: currentDate, score: Math.round(scaledNEV * 10) / 10 }
  ];
  
  // Get the relevant industry benchmark if industry is provided
  const relevantBenchmark = industry ? getIndustryBenchmark(industry) : null;

  return {
    // Main category scores with new category names
    ea: eaScore,
    elv: elvScore,
    l: lScore,
    tb: tbScore,
    bc: bcScore,
    
    // Include old category names for backward compatibility
    ee: eaScore,
    lq: elvScore,
    fc: lScore,
    
    totalNEV: Math.round(scaledNEV * 10) / 10,
    overallScore: Math.round(nevScore * 10) / 10,
    nev: Math.round(scaledNEV * 10) / 10,
    eroi: Math.min(5.0, Math.round(eroi * 100) / 100), // Round to 2 decimal places and cap at 5.0
    rating,
    certification,
    recommendations,
    sentimentAnalysis: {
      dominantEmotions: dominantEmotions,
      sentimentScore: Object.values(textSentiments).reduce((acc, val) => acc + val.score, 0) / 
                      (Object.values(textSentiments).length || 1),
      detectedIssues: Array.from(new Set(
        Object.values(textSentiments).flatMap(s => s.tags)
      )).filter(tag => tag !== 'No specific sentiment detected')
    },
    pprAlert,
    submetrics: {
      emotionalHeatmap,
      textSentiments,
      // Generate more varied metrics based on actual scores
      ea: { 
        fandom: Math.min(10, boundedEaWeightedAvg * 2), 
        trust: Math.min(10, boundedTbWeightedAvg * 2), 
        referrals: Math.min(10, boundedEaWeightedAvg * 1.8) 
      },
      elv: { 
        redFlags: redFlags,
        brightSpots: brightSpots
      },
      l: { 
        projection: [
          Math.min(10, boundedLWeightedAvg * 2),
          Math.min(10, boundedLWeightedAvg * 2 + 0.3),
          Math.min(10, boundedLWeightedAvg * 2 - 0.4),
          Math.min(10, boundedLWeightedAvg * 2 - 1.0),
          Math.min(10, boundedLWeightedAvg * 2 - 1.4)
        ] 
      }
    },
    riskRadar: {
      community: Math.max(1, 10 - (boundedEaWeightedAvg * 2)),
      ethics: Math.max(1, 10 - (boundedTbWeightedAvg * 2)),
      funding: Math.max(1, 10 - (boundedBcWeightedAvg * 2)),
      innovation: Math.max(1, 10 - (boundedElvWeightedAvg * 2))
    },
    keyInsight: generateKeyInsight(boundedEaWeightedAvg, boundedElvWeightedAvg, boundedLWeightedAvg, boundedTbWeightedAvg),
    industryBenchmarks,
    historicalData,
    categories
  };
};

// Generate feedback for a specific category
const generateCategoryFeedback = (category: string, avg: number): string => {
  switch(category) {
    case 'ea':
      if (avg >= 4.5) return "Exceptional emotional connection!";
      if (avg >= 3.5) return "Strong emotional attachment.";
      if (avg >= 2.5) return "Moderate emotional connection.";
      if (avg >= 1.5) return "Weak emotional connection.";
      return "Critical: No emotional attachment.";
      
    case 'elv':
      if (avg >= 4.5) return "Exceptional longevity potential!";
      if (avg >= 3.5) return "Strong long-term relevance.";
      if (avg >= 2.5) return "Moderate competitive advantage.";
      if (avg >= 1.5) return "Limited longevity indicators.";
      return "High risk of market obsolescence.";
      
    case 'l':
      if (avg >= 4.5) return "Exceptionally committed founders!";
      if (avg >= 3.5) return "Strong founder commitment.";
      if (avg >= 2.5) return "Moderate founder involvement.";
      if (avg >= 1.5) return "Limited founder engagement.";
      return "Concerning founder commitment issues.";
      
    case 'tb':
      if (avg >= 4.5) return "Exceptional team dynamics!";
      if (avg >= 3.5) return "Strong team culture.";
      if (avg >= 2.5) return "Moderate team cohesion.";
      if (avg >= 1.5) return "Team development needed.";
      return "Critical team building issues.";
      
    default:
      return "Assessment complete.";
  }
};

// Generate dynamic red flags based on responses
const generateRedFlags = (
  responses: SurveyResponse,
  eaAvg: number,
  elvAvg: number,
  lAvg: number
): string[] => {
  const redFlags: string[] = [];
  
  if (eaAvg < 3.0) redFlags.push("Weak emotional connection");
  if (elvAvg < 3.0) redFlags.push("Limited innovation potential");
  if (lAvg < 3.0) redFlags.push("Founder commitment concerns");
  
  if (responses[7] && getNumericValue(responses[7]) < 3) {
    redFlags.push("Market relevance uncertainty");
  }
  
  if (responses[8] && getNumericValue(responses[8]) < 3) {
    redFlags.push("Low switching costs");
  }
  
  // Ensure we have 2-3 red flags
  if (redFlags.length === 0) {
    redFlags.push("Market volatility risk");
    redFlags.push("Competitive pressure");
  } else if (redFlags.length === 1) {
    redFlags.push("Implementation challenges");
  }
  
  return redFlags.slice(0, 3);
};

// Generate dynamic bright spots based on responses
const generateBrightSpots = (
  responses: SurveyResponse,
  eaAvg: number,
  elvAvg: number,
  lAvg: number,
  tbAvg: number
): string[] => {
  const brightSpots: string[] = [];
  
  if (eaAvg >= 3.5) brightSpots.push("Strong user engagement");
  if (elvAvg >= 3.5) brightSpots.push("Clear differentiators");
  if (lAvg >= 4.0) brightSpots.push("Strong leadership vision");
  if (tbAvg >= 4.0) brightSpots.push("Effective team collaboration");
  
  if (responses[3] && getNumericValue(responses[3]) >= 4) {
    brightSpots.push("High user loyalty");
  }
  
  if (responses[12] && getNumericValue(responses[12]) >= 4) {
    brightSpots.push("Positive team culture");
  }
  
  if (responses[16] && getNumericValue(responses[16]) >= 4) {
    brightSpots.push("Financial stability");
  }
  
  // Ensure we have at least 3 bright spots
  if (brightSpots.length < 3) {
    const defaults = [
      "Growth potential", 
      "Market opportunity", 
      "Adaptability"
    ];
    
    brightSpots.push(...defaults.slice(0, 3 - brightSpots.length));
  }
  
  return brightSpots.slice(0, 5); // Return up to 5 bright spots
};

// Generate recommendations based on responses
const generateRecommendations = (
  responses: SurveyResponse,
  eaAvg: number,
  elvAvg: number,
  lAvg: number,
  tbAvg: number
): string[] => {
  const recommendations: string[] = [];

  // EA recommendations
  if (eaAvg < 3.5) {
    recommendations.push("Strengthen emotional connection with customers through personalized engagement (+2 EA)");
    recommendations.push("Create community-building initiatives to foster deeper attachment (+2 EA)");
  }

  // LQ recommendations
  if (elvAvg < 3.5) {
    recommendations.push("Increase innovation rate to stay ahead of competitors (+2 LQ)");
    recommendations.push("Develop higher switching costs to increase user retention (+1 LQ)");
  }

  // FC recommendations
  if (lAvg < 3.5) {
    recommendations.push("Increase founder visibility in day-to-day operations (+2 FC)");
    recommendations.push("Develop clearer communication of founders' long-term vision (+1 FC)");
  }

  // TB recommendations
  if (tbAvg < 3.5) {
    recommendations.push("Implement team-building initiatives to improve collaboration (+2 TB)");
    recommendations.push("Create diversity and inclusion programs to strengthen team dynamics (+1 TB)");
  }

  // If we have more than 3 recommendations, return only the top 3
  // If we have less than 3, add generic ones
  if (recommendations.length < 3) {
    const genericRecs = [
      "Focus on strategic market positioning to enhance competitive advantage (+1 LQ)",
      "Develop a stronger narrative around your unique value proposition (+1 EA)",
      "Implement regular feedback mechanisms with early users (+1 FC)"
    ];
    
    recommendations.push(...genericRecs.slice(0, 3 - recommendations.length));
  }
  
  return recommendations.slice(0, 3);
};

// Generate key insight based on scores
const generateKeyInsight = (eaAvg: number, elvAvg: number, lAvg: number, tbAvg: number): string => {
  if (eaAvg > 4.0 && elvAvg < 3.0) {
    return "Strong emotional connection exists but long-term relevance is uncertain. Focus on innovation and competitive differentiation.";
  } else if (elvAvg > 4.0 && eaAvg < 3.0) {
    return "Strong longevity potential but lacks emotional connection. Build community engagement and project identity.";
  } else if (lAvg < 3.0) {
    return "Founder commitment concerns detected. Increasing founder involvement and accessibility would strengthen overall value.";
  } else if (tbAvg < 3.0) {
    return "Team development needed. Focus on culture-building and retention strategies for key team members.";
  } else if ((eaAvg + elvAvg + lAvg + tbAvg) / 4 > 4.0) {
    return "Well-balanced performance across all metrics. Build on strengths to reach exceptional status.";
  } else {
    return "Multiple improvement areas identified. Prioritize recommendations for maximum impact.";
  }
};
